import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from 'helpers/cn';
import { Kbd } from './Kbd';
import { usePlatformKeys, type ShortcutKey } from './use-platform-keys';

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'children'> & {
  /** Required: an icon has no text of its own, so this is its accessible name and its tooltip. */
  label: string;
  icon: ReactNode;
  size?: 24 | 28 | 32 | 36;
  /** Sets aria-pressed, for a button that toggles a state rather than running an action. */
  pressed?: boolean;
  /** Shown in the tooltip and announced through aria-keyshortcuts. */
  shortcut?: readonly ShortcutKey[];
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
};

const SIZES = { 24: 'size-6', 28: 'size-7', 32: 'size-8', 36: 'size-9' };

/**
 * Shared focus ring for the console controls (the editor's, and the AI chat's wherever it opens):
 * 2px accent, 2px clear of the edge.
 */
export const FOCUS_RING = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

/**
 * A ghost button holding only an icon. The tooltip carries its own provider, so it works
 * inside a dialog or a panel without depending on where the nearest one happens to sit.
 *
 * Unavailable buttons are better marked with aria-disabled than disabled: they stay
 * focusable, so a keyboard user still finds them and hears why nothing happens. The caller
 * guards its own click in that case.
 */
export const IconButton = (props: IconButtonProps) => {
  const { label, icon, size = 36, pressed, shortcut, tooltipSide, className, type, ...rest } = props;
  const { aria } = usePlatformKeys();
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type={type ?? 'button'}
            aria-label={label}
            aria-pressed={pressed}
            aria-keyshortcuts={shortcut ? aria(shortcut) : undefined}
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-lg text-dim transition-colors duration-100',
              'hover:bg-field hover:text-foreground disabled:pointer-events-none disabled:opacity-50',
              'aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-transparent',
              'aria-pressed:bg-field aria-pressed:text-accent',
              '[&_svg]:size-4',
              FOCUS_RING,
              SIZES[size],
              className,
            )}
            {...rest}
          >
            {icon}
          </button>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide} className="flex items-center gap-2">
          {label}
          {shortcut && <Kbd keys={shortcut} tone="onAccent" />}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
